import {useEffect, useRef} from "react";
import {Chart} from "chart.js/auto";
import {calculateVisualScore} from "../utils/analysisFunctions";
import {formatEval} from "./EvalBar";

// Evaluation graph over the whole game. Click a point to jump to that position.
const AnalysisChart = ({data, currentIndex, onPointClick}) => {
    const chartRef = useRef(null);
    const canvasRef = useRef(null)
    const clickRef = useRef(onPointClick);
    clickRef.current = onPointClick;

    useEffect(() => {
        if (chartRef.current) {
            chartRef.current.destroy();
        }

        const displayScores = data.map((evalDiff) => calculateVisualScore(evalDiff))

        chartRef.current = new Chart(
            canvasRef.current.getContext("2d"),
            {
                type: 'line',
                options: {
                    animation: false,
                    maintainAspectRatio: false,
                    interaction: {mode: "index", intersect: false},
                    layout: {padding: {top: 4, bottom: 4}},
                    plugins: {
                        legend: {display: false},
                        tooltip: {
                            displayColors: false,
                            backgroundColor: "rgba(46, 58, 44, 0.92)",
                            padding: 8,
                            callbacks: {
                                title: (items) => items.length ? (items[0].dataIndex === 0 ? "Start" : `Move ${Math.ceil(items[0].dataIndex / 2)}`) : "",
                                label: (context) => formatEval(data[context.dataIndex])
                            }
                        }
                    },
                    scales: {
                        x: {display: false},
                        y: {
                            display: false,
                            min: -105,
                            max: 105
                        }
                    },
                    onClick: (event, elements) => {
                        if (elements.length > 0) clickRef.current(elements[0].index)
                    },
                    onHover: (event, elements) => {
                        const canvas = event.native?.target;
                        if (canvas) canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
                    }
                },
                data: {
                    labels: displayScores.map((_, i) => i),
                    datasets: [
                        {
                            data: displayScores,
                            tension: 0.25,
                            borderColor: "#5c8b4a",
                            borderWidth: 1.5,
                            pointRadius: 0,
                            pointHoverRadius: 4,
                            pointBackgroundColor: "#e2849a",
                            fill: {
                                target: "origin",
                                above: "rgba(255, 253, 247, 0.95)",
                                below: "rgba(46, 58, 44, 0.78)"
                            }
                        }
                    ]
                }
            }
        )

        return () => {
            chartRef.current?.destroy();
            chartRef.current = null;
        };
    }, [data]);

    // Mark the current position without rebuilding the chart
    useEffect(() => {
        const chart = chartRef.current;
        if (!chart) return;
        const dataset = chart.data.datasets[0];
        dataset.pointRadius = dataset.data.map((_, i) => (i === currentIndex ? 5 : 0));
        dataset.pointBackgroundColor = "#e2849a";
        dataset.pointBorderColor = "#fff";
        dataset.pointBorderWidth = 2;
        chart.update("none");
    }, [currentIndex, data]);

    return (
        <div className="chart-box" style={{background: "var(--paper-2)", border: "1px solid var(--line)", borderRadius: 12, margin: "0 0.9rem 0.9rem"}}>
            <canvas ref={canvasRef} aria-label="Evaluation graph"/>
        </div>
    )
}

export default AnalysisChart;
